import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { catchError, map, of } from "rxjs";
import { LektoAuthService, OfflineSelection } from "@frontends-v3/shared-core";
import { NzModalRef } from "ng-zorro-antd/modal";

import {
  ProfessorAssignmentItem,
  ProfessorAssignments
} from "../../../core/models/content-assignment.interface";
import { ContentAssignmentService } from "../../../core/services/content-assignment.service";
import { AssignmentCardComponent } from "../../../shared/components/assignment-card/assignment-card.component";
import { AssignmentCardViewModel } from "../../../shared/components/assignment-card/assignment-card.interface";
import {
  buildAssignmentCardViewModel,
  classifyAssignment,
  showsOriginSchool
} from "../../../shared/utils/professor-assignment-card.util";

export interface OfflinePickerOption {
  selection: OfflineSelection;
  vm: AssignmentCardViewModel;
}

/**
 * DEV-966 (CA 02/03) — janela de escolha da atribuição que ficará disponível no Modo Offline.
 * A fonte é "Minhas" (`professor/assignments`) para qualquer perfil: só as atribuições das quais o
 * usuário participa, que é exatamente o que o servidor aceita ao ligar o modo. A visão do domínio
 * do gestor (`manager/assignments`) listaria atribuições que ele não pode levar (404 ao ligar).
 * Cada opção reusa o card da listagem (mesmas informações: tipo, situação, progresso, turma,
 * atribuído por, prazo); sem escolha, confirmar fica indisponível; cancelar fecha sem ativar.
 * Fecha com a `OfflineSelection` escolhida ou `null`.
 */
@Component({
  selector: "app-offline-assignment-picker-modal",
  standalone: true,
  imports: [AssignmentCardComponent],
  templateUrl: "./offline-assignment-picker-modal.component.html",
  styleUrl: "./offline-assignment-picker-modal.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfflineAssignmentPickerModalComponent {
  private readonly modalRef =
    inject<NzModalRef<OfflineAssignmentPickerModalComponent, OfflineSelection | null>>(NzModalRef);
  private readonly assignmentService = inject(ContentAssignmentService);
  private readonly authService = inject(LektoAuthService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isLoading = signal<boolean>(true);
  protected readonly hasError = signal<boolean>(false);
  protected readonly options = signal<OfflinePickerOption[]>([]);
  protected readonly selectedId = signal<string | null>(null);

  protected readonly selected = computed<OfflinePickerOption | null>(
    () =>
      this.options().find((option) => option.selection.idAssignment === this.selectedId()) ?? null
  );
  protected readonly isEmpty = computed<boolean>(
    () => !this.isLoading() && !this.hasError() && this.options().length === 0
  );

  constructor() {
    this.load();
  }

  protected load(): void {
    this.isLoading.set(true);
    this.hasError.set(false);

    this.assignmentService
      .getProfessorAssignments()
      .pipe(
        map((groups: ProfessorAssignments) => [
          ...(groups?.active ?? []),
          ...(groups?.finished ?? [])
        ]),
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((items) => {
        if (!items) {
          this.hasError.set(true);
          this.isLoading.set(false);
          return;
        }
        this.options.set(items.map((item) => this.toOption(item)));
        this.isLoading.set(false);
      });
  }

  private toOption(item: ProfessorAssignmentItem): OfflinePickerOption {
    return {
      selection: { idAssignment: item.idAssignment, idContentTemplate: item.idContentTemplate },
      vm: buildAssignmentCardViewModel(
        item,
        classifyAssignment(item, false),
        // Mesmo card da tela de Atribuições (DEV-966 CA 02): o selo de escola segue o perfil.
        showsOriginSchool(this.authService.authProfile())
      )
    };
  }

  protected onSelect(option: OfflinePickerOption): void {
    this.selectedId.set(option.selection.idAssignment);
  }

  protected onConfirm(): void {
    const selected = this.selected();
    if (!selected) return;
    this.modalRef.close(selected.selection);
  }

  protected onCancel(): void {
    this.modalRef.close(null);
  }
}
